import { apiService } from './apiService'
import { storageWrapper } from '../utils/storageWrapper'

// Storage keys and TTL
const STORAGE_KEY = 'eams_amenity_cache'
const TTL_MS = 60 * 60 * 1000 // 1 hour

// Cache state
const cache = {
  amenitiesWithOptions: { data: null, loaded: false },
  // Legacy caches for fallback
  amenities: { data: null, loaded: false },
  amenityTypes: { data: null, loaded: false },
  amenityOptions: { data: null, loaded: false }
}

// Promise for in-flight request (allows multiple callers to await the same fetch)
let amenitiesLoadingPromise = null

/**
 * Load cache from localStorage if valid (not expired)
 */
const loadFromStorage = () => {
  try {
    const stored = storageWrapper.getItem(STORAGE_KEY)
    if (!stored) return false

    const parsed = JSON.parse(stored)
    const now = Date.now()

    // Check TTL
    if (!parsed.cachedAt || (now - parsed.cachedAt) > TTL_MS) {
      storageWrapper.removeItem(STORAGE_KEY)
      return false
    }

    // Restore cache from storage
    if (parsed.data) {
      cache.amenitiesWithOptions.data = parsed.data
      cache.amenitiesWithOptions.loaded = true
      return true
    }
  } catch (e) {
    console.error('Error loading amenity cache from storage:', e)
    storageWrapper.removeItem(STORAGE_KEY)
  }
  return false
}

/**
 * Save cache to localStorage
 */
const saveToStorage = () => {
  try {
    const dataToStore = {
      cachedAt: Date.now(),
      data: cache.amenitiesWithOptions.data
    }
    storageWrapper.setItem(STORAGE_KEY, JSON.stringify(dataToStore))
  } catch (e) {
    console.error('Error saving amenity cache to storage:', e)
  }
}

/**
 * Listen for changes from other tabs
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // Avoid overwriting in-memory cache from storage while a fetch is in progress
    if (event.key === STORAGE_KEY && !amenitiesLoadingPromise) {
      loadFromStorage()
    }
  })
}

// Load from storage on module initialization
loadFromStorage()

/**
 * Fetch amenities with options from the combined endpoint
 */
const fetchAmenitiesWithOptions = async () => {
  // Return cached data if already loaded
  if (cache.amenitiesWithOptions.loaded && cache.amenitiesWithOptions.data) {
    return cache.amenitiesWithOptions.data
  }

  // If already loading, return existing promise (multiple callers await the same fetch)
  if (amenitiesLoadingPromise) {
    return amenitiesLoadingPromise
  }

  // Create and store the promise
  amenitiesLoadingPromise = (async () => {
    try {
      const response = await apiService.get('/Amenities/with-options')
      const amenities = response?.amenities || response || []
      cache.amenitiesWithOptions.data = amenities
      cache.amenitiesWithOptions.loaded = true
      saveToStorage()
      return amenities
    } catch (error) {
      console.error('Failed to fetch amenities with options:', error?.message || error)
      // Fallback to individual endpoints
      return await fetchAmenitiesWithOptionsLegacy()
    } finally {
      amenitiesLoadingPromise = null
    }
  })()

  return amenitiesLoadingPromise
}

/**
 * Legacy fallback: fetch amenities, types, and options separately
 */
const fetchAmenitiesWithOptionsLegacy = async () => {
  try {
    const [amenitiesResponse, typesResponse, optionsResponse] = await Promise.all([
      apiService.get('/Amenities'),
      apiService.get('/enums/amenity-types'),
      apiService.get('/AmenityOptions')
    ])

    const amenities = Array.isArray(amenitiesResponse) ? amenitiesResponse : []
    const types = Array.isArray(typesResponse) ? typesResponse : []
    const options = Array.isArray(optionsResponse) ? optionsResponse : []

    // Merge data
    const merged = amenities.map(amenity => {
      const amenityType = types.find(type => type.id === amenity.amenityTypeId)
      const amenityOptions = options.filter(option => option.amenityId === amenity.id)
      return {
        ...amenity,
        amenityTypeName: amenityType?.name || null,
        options: amenityOptions
      }
    })

    cache.amenitiesWithOptions.data = merged
    cache.amenitiesWithOptions.loaded = true
    saveToStorage()
    return merged
  } catch (error) {
    console.error('Failed to fetch amenities (legacy):', error?.message || error)
    return []
  }
}

// Public API - maintains backwards compatibility

export const getAmenities = async () => {
  const data = await fetchAmenitiesWithOptions()
  // Return just the amenity data without options
  return data.map(({ options, ...amenity }) => amenity)
}

export const getAmenityTypes = async () => {
  const data = await fetchAmenitiesWithOptions()
  // Extract unique amenity types
  const typesMap = new Map()
  data.forEach(amenity => {
    if (amenity.amenityTypeId && amenity.amenityTypeName) {
      typesMap.set(amenity.amenityTypeId, {
        id: amenity.amenityTypeId,
        name: amenity.amenityTypeName
      })
    }
  })
  return Array.from(typesMap.values())
}

export const getAmenityOptions = async () => {
  const data = await fetchAmenitiesWithOptions()
  // Extract all options with their amenityId
  return data.flatMap(amenity =>
    (amenity.options || []).map(option => ({
      ...option,
      amenityId: amenity.id
    }))
  )
}

/**
 * Get amenities with their type names merged
 * @returns {Promise<Array>} Array of amenities with amenityTypeName property
 */
export const getAmenitiesWithTypes = async () => {
  const data = await fetchAmenitiesWithOptions()
  // Return amenities with type names but without options
  return data.map(({ options, ...amenity }) => amenity)
}

/**
 * Get amenities with their options merged
 * @returns {Promise<Array>} Array of amenities with options property
 */
export const getAmenitiesWithOptions = async () => {
  const data = await fetchAmenitiesWithOptions()
  // Return amenities with options but without type info
  return data.map(amenity => ({
    id: amenity.id,
    name: amenity.name,
    amenityTypeId: amenity.amenityTypeId,
    helpText: amenity.helpText,
    options: amenity.options || []
  }))
}

/**
 * Get amenities with both types and options merged
 * @returns {Promise<Array>} Array of amenities with amenityTypeName and options properties
 */
export const getAmenitiesWithTypesAndOptions = async () => {
  return await fetchAmenitiesWithOptions()
}

export const getCharacteristicAmenities = async () => {
  const amenities = await fetchAmenitiesWithOptions()
  return amenities.filter(amenity =>
    amenity.amenityTypeName?.toLowerCase() === 'characteristic'
  )
}

export const getSafetyAmenities = async () => {
  const amenities = await fetchAmenitiesWithOptions()
  return amenities.filter(amenity =>
    amenity.amenityTypeName?.toLowerCase() === 'safety'
  )
}

export const getFeatureAmenities = async () => {
  const amenities = await fetchAmenitiesWithOptions()
  return amenities.filter(amenity =>
    amenity.amenityTypeName?.toLowerCase() === 'amenity'
  )
}

export const getAdditionalAmenities = async () => {
  const amenities = await fetchAmenitiesWithOptions()
  return amenities.filter(amenity =>
    amenity.amenityTypeName?.toLowerCase() === 'additional'
  )
}

/**
 * Get amenities dynamically grouped by type (no hardcoding)
 * @returns {Promise<Array>} Array of grouped amenity sections with types, names, and amenities
 */
export const getAmenitiesGroupedByType = async () => {
  const amenities = await fetchAmenitiesWithOptions()

  // Group amenities by amenityTypeName
  const grouped = {}

  amenities.forEach(amenity => {
    const typeName = amenity.amenityTypeName || 'Other'
    const typeId = amenity.amenityTypeId

    if (!grouped[typeName]) {
      grouped[typeName] = {
        typeId,
        typeName,
        amenities: []
      }
    }

    grouped[typeName].amenities.push(amenity)
  })

  // Convert to array and sort alphabetically by type name
  return Object.values(grouped).sort((a, b) => a.typeName.localeCompare(b.typeName))
}

export const clearAmenitiesCache = () => {
  cache.amenitiesWithOptions.data = null
  cache.amenitiesWithOptions.loaded = false
  storageWrapper.removeItem(STORAGE_KEY)
}

export const clearAmenityTypesCache = () => {
  // Types are derived from amenities, clear the main cache
  clearAmenitiesCache()
}

export const clearAmenityOptionsCache = () => {
  // Options are included in amenities, clear the main cache
  clearAmenitiesCache()
}

export const clearAllAmenityCaches = () => {
  clearAmenitiesCache()
}

export const getAmenitiesCacheStatus = () => {
  return {
    loaded: cache.amenitiesWithOptions.loaded,
    loading: amenitiesLoadingPromise !== null,
    hasData: cache.amenitiesWithOptions.data !== null
  }
}

export const getAmenityTypesCacheStatus = () => {
  return getAmenitiesCacheStatus()
}

export const getAmenityOptionsCacheStatus = () => {
  return getAmenitiesCacheStatus()
}

// Export for preloading
export const preloadAmenities = fetchAmenitiesWithOptions
